import React, { useState } from "react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import ScanAuditTimeline from "@/modules/racket_lab_admin/ScanAuditTimeline";
import useAdminMutation from "@/hooks/useAdminMutation";
import { api } from "@/lib/api";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminResolveViewer — Sub-sprint C.
 *
 * Internal admin/staff Resolve Viewer. JWT-gated. Two-tab result:
 *   - JSON tab (full resolve response via JSONInspector)
 *   - Scan timeline tab (looks up the token doc to render scan_audit[])
 *
 * Optional token_id input lets the operator skip a reverse lookup; if
 * absent we still surface the resolve JSON but the timeline tab will
 * be empty.
 */
const Tab = ({ active, onClick, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-t-md border-b-2 px-3 py-1.5 text-xs font-medium ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
  >
    {label}
  </button>
);

const RLAdminResolveViewer = () => {
  const t = useRacketLabAdminT();
  const [tokenStr, setTokenStr] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [tab, setTab] = useState("json");
  const [response, setResponse] = useState(null);
  const [scanAudit, setScanAudit] = useState([]);
  const [err, setErr] = useState(null);
  const { run, loading } = useAdminMutation();

  const onResolve = async () => {
    setErr(null);
    setResponse(null);
    setScanAudit([]);
    if (!tokenStr.trim()) {
      setErr({ detail: t("resolve.error_required") });
      return;
    }
    try {
      const r = await api.get(`/qr-tokens/${encodeURIComponent(tokenStr.trim())}/resolve`);
      setResponse(r.data);
    } catch (e) {
      setErr({ status: e?.response?.status, detail: e?.response?.data?.detail || e?.message });
      return;
    }
    if (tokenId.trim()) {
      try {
        const r2 = await api.get(`/admin/qr-tokens/${encodeURIComponent(tokenId.trim())}`);
        setScanAudit(r2.data?.scan_audit || []);
      } catch (e) {
        // soft-fail: timeline stays empty
        setScanAudit([]);
      }
    }
    // silence unused
    void run;
    void loading;
  };

  return (
    <div data-testid="rl-admin-resolve-viewer-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("resolve.page_header")}</h1>
        <p className="text-xs text-slate-500">{t("resolve.subtitle")}</p>
      </header>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-slate-700">{t("resolve.token_label")}</span>
            <input
              data-testid="rl-resolve-viewer-token-input"
              value={tokenStr}
              onChange={(e) => setTokenStr(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
              placeholder={t("resolve.token_placeholder")}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-slate-700">{t("common.labels.entity")} ID</span>
            <input
              data-testid="rl-resolve-viewer-token-id-input"
              value={tokenId}
              onChange={(e) => setTokenId(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
              placeholder="uuid"
            />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-[10px] text-amber-700">{t("banners.pilot_mode_secondary")}</p>
          <button
            type="button"
            data-testid="rl-resolve-viewer-submit"
            onClick={onResolve}
            disabled={!tokenStr.trim()}
            className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${tokenStr.trim() ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-300 cursor-not-allowed"}`}
          >
            {t("resolve.submit")}
          </button>
        </div>
      </section>

      {err && (
        <div data-testid="rl-resolve-viewer-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {t("dashboard.error_prefix")} {err.status || ""}: {err.detail || t("dashboard.error_unknown")}
        </div>
      )}

      {response && (
        <section data-testid="rl-resolve-viewer-result" className="rounded-md border border-slate-200 bg-white">
          <div className="flex gap-2 border-b border-slate-200 px-3">
            <Tab active={tab === "json"} onClick={() => setTab("json")} label="JSON" testId="rl-resolve-viewer-tab-json" />
            <Tab active={tab === "timeline"} onClick={() => setTab("timeline")} label={t("ticket_actions.audit_timeline_title")} testId="rl-resolve-viewer-tab-timeline" />
          </div>
          <div className="p-3">
            {tab === "json" && (
              <JSONInspector value={response} dataTestId="rl-resolve-viewer-json" />
            )}
            {tab === "timeline" && (
              <ScanAuditTimeline entries={scanAudit} dataTestId="rl-resolve-viewer-timeline" />
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default RLAdminResolveViewer;
